import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import {
  Plus, PencilSimple, Trash, ArrowDown, ArrowUp, Pulse, Package, MagnifyingGlass, ClockCounterClockwise, Warning,
} from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import Pagination from "../../components/Pagination";

const UNITS = ["tablet", "capsule", "strip", "bottle", "ml", "g", "kg", "sachet", "piece"];

function ItemDialog({ trigger, initial, onSaved }) {
  const editing = Boolean(initial?.id);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", sku: "", unit: "tablet", current_qty: 0, reorder_level: 0, price: 0,
    supplier_name: "", notes: "", active: true,
  });

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        name: initial.name || "", sku: initial.sku || "", unit: initial.unit || "tablet",
        current_qty: initial.current_qty || 0, reorder_level: initial.reorder_level || 0,
        price: initial.price || 0, supplier_name: initial.supplier_name || "",
        notes: initial.notes || "", active: initial.active !== false,
      } : {
        name: "", sku: "", unit: "tablet", current_qty: 0, reorder_level: 0, price: 0,
        supplier_name: "", notes: "", active: true,
      });
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setNum = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        // master fields only — quantity is adjusted via movements
        const { current_qty, ...master } = form;
        await api.patch(`/inventory/items/${initial.id}`, master);
        toast.success("Item updated");
      } else {
        await api.post("/inventory/items", form);
        toast.success("Item added");
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl bg-ivory" data-testid="item-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            {editing ? "Edit item" : "New medicine / stock item"}
          </DialogTitle>
          <DialogDescription>Master catalogue details — quantity is adjusted via movements below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Name *</Label>
            <Input value={form.name} onChange={set("name")} required className="bg-cream h-11 mt-1" data-testid="item-name"/>
          </div>
          <div>
            <Label>SKU / code</Label>
            <Input value={form.sku} onChange={set("sku")} className="bg-cream h-11 mt-1" data-testid="item-sku"/>
          </div>
          <div>
            <Label>Unit</Label>
            <Select value={form.unit} onValueChange={(v) => set("unit")(v)}>
              <SelectTrigger className="bg-cream h-11 mt-1" data-testid="item-unit"><SelectValue/></SelectTrigger>
              <SelectContent>
                {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!editing && (
            <div>
              <Label>Opening quantity</Label>
              <Input type="number" min="0" step="any" value={form.current_qty} onChange={setNum("current_qty")} className="bg-cream h-11 mt-1" data-testid="item-qty"/>
            </div>
          )}
          <div>
            <Label>Reorder level</Label>
            <Input type="number" min="0" step="any" value={form.reorder_level} onChange={setNum("reorder_level")} className="bg-cream h-11 mt-1" data-testid="item-reorder"/>
          </div>
          <div>
            <Label>Selling price (₹ / unit)</Label>
            <Input type="number" min="0" step="any" value={form.price} onChange={setNum("price")} className="bg-cream h-11 mt-1" data-testid="item-price"/>
          </div>
          <div className="sm:col-span-2">
            <Label>Supplier</Label>
            <Input value={form.supplier_name} onChange={set("supplier_name")} className="bg-cream h-11 mt-1" data-testid="item-supplier"/>
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} className="bg-cream mt-1 min-h-[80px]" data-testid="item-notes"/>
          </div>
          <DialogFooter className="sm:col-span-2">
            <button type="submit" disabled={saving} className="rounded-full bg-forest text-cream px-6 py-2.5 disabled:opacity-50" data-testid="item-save">
              {saving ? "Saving…" : editing ? "Save changes" : "Add item"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MovementDialog({ trigger, item, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState("in");
  const [form, setForm] = useState({
    quantity: 0, reason: "", notes: "", batch_no: "", expiry_date: "", purchase_cost: 0,
  });

  useEffect(() => {
    if (open) {
      setType("in");
      setForm({ quantity: 0, reason: "", notes: "", batch_no: "", expiry_date: "", purchase_cost: 0 });
    }
  }, [open]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setNum = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) }));

  const submit = async (e) => {
    e.preventDefault();
    const qty = Number(form.quantity || 0);
    if (qty <= 0 && type !== "adjust") { toast.error("Enter a quantity greater than 0"); return; }
    if (qty < 0) { toast.error("Quantity cannot be negative"); return; }
    setSaving(true);
    try {
      await api.post(`/inventory/items/${item.id}/movements`, { type, ...form, quantity: qty });
      toast.success(
        type === "in" ? `Stock added — ${item.name}`
        : type === "out" ? `Stock issued — ${item.name}`
        : `Stock adjusted — ${item.name}`
      );
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Movement failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl bg-ivory" data-testid="movement-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            Update stock — {item?.name}
          </DialogTitle>
          <DialogDescription>Record stock-in, stock-out or a physical-count adjustment.</DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2">
          Currently in stock: <b className="text-forest">{item?.current_qty} {item?.unit}</b>
        </div>
        <form onSubmit={submit} className="grid gap-4">
          <div>
            <Label>Movement type</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[
                { v: "in", label: "Stock in", icon: ArrowDown, tone: "bg-forest text-cream" },
                { v: "out", label: "Stock out", icon: ArrowUp, tone: "bg-terracotta text-cream" },
                { v: "adjust", label: "Set exact", icon: Pulse, tone: "bg-sand text-forest" },
              ].map(opt => (
                <button key={opt.v} type="button" onClick={() => setType(opt.v)}
                  className={`rounded-xl px-3 py-3 text-sm flex flex-col items-center gap-1 border transition-all ${
                    type === opt.v ? `${opt.tone} border-transparent` : "bg-cream border-border/60 hover:border-forest/40"
                  }`}
                  data-testid={`movement-type-${opt.v}`}>
                  <opt.icon size={18}/>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{type === "adjust" ? "New exact quantity" : "Quantity"} *</Label>
              <Input type="number" min="0" step="any" value={form.quantity} onChange={setNum("quantity")} required className="bg-cream h-11 mt-1" data-testid="movement-qty"/>
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={form.reason} onChange={set("reason")}
                placeholder={type === "in" ? "Purchase / return" : type === "out" ? "Dispensed / wastage" : "Physical count"}
                className="bg-cream h-11 mt-1" data-testid="movement-reason"/>
            </div>
          </div>
          {type === "in" && (
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Batch no.</Label>
                <Input value={form.batch_no} onChange={set("batch_no")} className="bg-cream h-11 mt-1" data-testid="movement-batch"/>
              </div>
              <div>
                <Label>Expiry date</Label>
                <Input type="date" value={form.expiry_date} onChange={set("expiry_date")} className="bg-cream h-11 mt-1" data-testid="movement-expiry"/>
              </div>
              <div>
                <Label>Purchase cost (₹)</Label>
                <Input type="number" min="0" step="any" value={form.purchase_cost} onChange={setNum("purchase_cost")} className="bg-cream h-11 mt-1" data-testid="movement-cost"/>
              </div>
            </div>
          )}
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} className="bg-cream mt-1 min-h-[70px]" data-testid="movement-notes"/>
          </div>
          <DialogFooter>
            <button type="submit" disabled={saving} className="rounded-full bg-forest text-cream px-6 py-2.5 disabled:opacity-50" data-testid="movement-save">
              {saving ? "Saving…" : "Record movement"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ trigger, item }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (open && item?.id) {
      api.get(`/inventory/items/${item.id}/movements`).then(r => setRows(r.data));
    }
  }, [open, item]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl bg-ivory" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Movement history — {item?.name}</DialogTitle>
          <DialogDescription>Complete audit trail of stock-in, stock-out and adjustment events.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">No movements recorded yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="p-3">When</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Qty</th>
                  <th className="p-3">Balance</th>
                  <th className="p-3">Reason / batch</th>
                  <th className="p-3">By</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(m => (
                  <tr key={m.id} className="border-t border-border/40 align-top" data-testid={`movement-row-${m.id}`}>
                    <td className="p-3 text-xs whitespace-nowrap">{new Date(m.created_at).toLocaleString()}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] ${
                        m.type === "in" ? "bg-forest text-cream" : m.type === "out" ? "bg-terracotta text-cream" : "bg-sand text-forest"
                      }`}>{m.type}</span>
                    </td>
                    <td className="p-3 font-mono text-xs">{m.type === "out" ? "-" : m.type === "in" ? "+" : "="}{m.quantity}</td>
                    <td className="p-3 font-mono text-xs">{m.qty_before} → <b>{m.qty_after}</b></td>
                    <td className="p-3 text-xs">
                      {m.reason || "—"}
                      {m.batch_no && <div className="text-[10px] text-muted-foreground">Batch {m.batch_no}{m.expiry_date ? ` · exp ${m.expiry_date}` : ""}</div>}
                      {m.notes && <div className="text-[10px] text-muted-foreground italic mt-0.5">{m.notes}</div>}
                    </td>
                    <td className="p-3 text-xs">{m.created_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Stock() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | low | out
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/items");
      setRows(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and its movement history?`)) return;
    await api.delete(`/inventory/items/${id}`);
    toast.success("Item removed");
    load();
  };

  const filtered = rows.filter(r => {
    if (filter === "low" && r.current_qty > r.reorder_level) return false;
    if (filter === "out" && r.current_qty > 0) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [r.name, r.sku, r.supplier_name, r.notes].some(v => (v || "").toLowerCase().includes(q));
  });

  useEffect(() => { setPage(1); }, [query, filter, pageSize]);
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const totals = {
    items: rows.length,
    low: rows.filter(r => r.current_qty <= r.reorder_level && r.current_qty > 0).length,
    out: rows.filter(r => r.current_qty <= 0).length,
    stockValue: rows.reduce((sum, r) => sum + (Number(r.current_qty) || 0) * (Number(r.price) || 0), 0),
  };

  return (
    <div data-testid="admin-stock" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-serif-display text-4xl">Stock & medicines</h1>
          <p className="text-muted-foreground mt-2">
            Master list of medicines with batch, supplier and a full movement log.
          </p>
        </div>
        {isAdmin && (
          <ItemDialog
            onSaved={load}
            trigger={
              <button className="rounded-full bg-forest text-cream px-5 py-2.5 flex items-center gap-2 self-start md:self-auto" data-testid="stock-add-btn">
                <Plus size={16}/> Add item
              </button>
            }
          />
        )}
      </div>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-forest text-cream rounded-2xl p-5" data-testid="stock-kpi-total">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-70">Tracked items</div>
          <div className="font-serif-display text-3xl mt-2">{totals.items}</div>
          <div className="text-xs mt-1 opacity-75"><Package size={12} className="inline"/> master catalogue</div>
        </div>
        <div className="bg-sand text-forest rounded-2xl p-5" data-testid="stock-kpi-low">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-70">Low stock</div>
          <div className="font-serif-display text-3xl mt-2">{totals.low}</div>
          <div className="text-xs mt-1 opacity-75">At or below reorder level</div>
        </div>
        <div className="bg-terracotta text-cream rounded-2xl p-5" data-testid="stock-kpi-out">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-70">Out of stock</div>
          <div className="font-serif-display text-3xl mt-2">{totals.out}</div>
          <div className="text-xs mt-1 opacity-75"><Warning size={12} className="inline"/> needs attention</div>
        </div>
        <div className="bg-ivory text-forest rounded-2xl p-5 border border-border/60" data-testid="stock-kpi-value">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-70">Inventory value</div>
          <div className="font-serif-display text-3xl mt-2">₹{Math.round(totals.stockValue).toLocaleString("en-IN")}</div>
          <div className="text-xs mt-1 opacity-75">Current qty × selling price</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, SKU, supplier…" className="bg-ivory h-11 pl-11" data-testid="stock-search"/>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full md:w-48 bg-ivory h-11" data-testid="stock-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All items</SelectItem>
            <SelectItem value="low">Low stock</SelectItem>
            <SelectItem value="out">Out of stock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs text-muted-foreground">
        Showing <b className="text-forest">{filtered.length}</b> of {rows.length} items
      </div>

      <div className="bg-ivory border border-border/50 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-4">Medicine / item</th>
              <th className="p-4">In stock</th>
              <th className="p-4">Reorder</th>
              <th className="p-4">Price</th>
              <th className="p-4">Supplier</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">
                {rows.length === 0 ? "No items yet. Add your first medicine to start tracking stock." : "No results match your filter."}
              </td></tr>
            ) : pageRows.map(r => {
              const isLow = r.current_qty <= r.reorder_level;
              const isOut = r.current_qty <= 0;
              return (
                <tr key={r.id} className="border-t border-border/40 align-top" data-testid={`stock-row-${r.id}`}>
                  <td className="p-4">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                      {r.sku || `ID ${r.id.slice(0, 8)}…`}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className={`font-mono ${isOut ? "text-destructive" : isLow ? "text-terracotta" : "text-forest"}`} data-testid={`stock-qty-${r.id}`}>
                      {r.current_qty} <span className="text-xs text-muted-foreground">{r.unit}</span>
                    </div>
                    {isOut && <span className="inline-block mt-1 text-[10px] uppercase tracking-[0.15em] text-destructive">Out</span>}
                    {!isOut && isLow && <span className="inline-block mt-1 text-[10px] uppercase tracking-[0.15em] text-terracotta">Low</span>}
                  </td>
                  <td className="p-4 text-xs">{r.reorder_level} {r.unit}</td>
                  <td className="p-4 text-xs">₹{r.price}</td>
                  <td className="p-4 text-xs">{r.supplier_name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <MovementDialog item={r} onSaved={load} trigger={
                        <button className="text-xs rounded-full bg-forest text-cream px-3 py-1.5 flex items-center gap-1" data-testid={`stock-move-${r.id}`}>
                          <Pulse size={12}/> Update qty
                        </button>
                      }/>
                      <HistoryDialog item={r} trigger={
                        <button className="text-xs rounded-full bg-sand text-forest px-3 py-1.5 flex items-center gap-1" data-testid={`stock-history-${r.id}`}>
                          <ClockCounterClockwise size={12}/> History
                        </button>
                      }/>
                      {isAdmin && (
                        <>
                          <ItemDialog initial={r} onSaved={load} trigger={
                            <button className="text-xs text-muted-foreground hover:text-forest p-1.5" data-testid={`stock-edit-${r.id}`}>
                              <PencilSimple size={14}/>
                            </button>
                          }/>
                          <button onClick={() => remove(r.id, r.name)} className="text-xs text-destructive hover:underline p-1.5" data-testid={`stock-delete-${r.id}`}>
                            <Trash size={14}/>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testId="stock-pagination"
        />
      )}
    </div>
  );
}
